import { clientAccess } from './clientAccess';
import { clientService } from './clientService';
import type {
  UserRecord,
  AllUsersReportParameters,
  AllUsersReportResult,
  ToolProgress,
  ToolResultFile,
} from '@/types/tools';
import type { Client } from '@/types';

interface ClientUsersResult {
  clientName: string;
  users: UserRecord[];
  error?: string;
}

/** One person from the client access Lambda's `users.reportPage`. */
interface ReportUser {
  username: string;
  email: string | null;
  isAdmin: boolean;
  status: string | null;
  enabled: boolean;
  createdAt: string | null;
  lastLoginAt?: string;
}

interface ReportPage {
  users: ReportUser[];
  paginationToken?: string;
}

export class AllUsersReportService {
  /**
   * Generate all users report for one or multiple clients
   */
  static async generateReport(
    parameters: AllUsersReportParameters,
    onProgress?: (progress: ToolProgress) => void
  ): Promise<{ result: AllUsersReportResult; files: ToolResultFile[] }> {
    const { clientNames, userTypeFilter, outputFormat, includeLastLogin = false } = parameters;

    try {
      onProgress?.({ current: 0, total: 100, message: 'Starting report generation...' });

      onProgress?.({ current: 5, total: 100, message: 'Loading client configurations...' });

      // Get all clients
      const allClients = await clientService.getAllClients();

      // Determine which clients to process
      let clientsToProcess: Client[];
      if (clientNames.length === 0) {
        // Empty array means "all clients"
        clientsToProcess = allClients;
      } else {
        clientsToProcess = allClients.filter((c) => clientNames.includes(c.name));
      }

      if (clientsToProcess.length === 0) {
        throw new Error('No clients found matching the selection');
      }

      onProgress?.({
        current: 10,
        total: 100,
        message: `Processing ${clientsToProcess.length} client(s)...`,
      });

      // Process each client
      const clientResults: ClientUsersResult[] = [];
      const failedClients: { clientName: string; error: string }[] = [];

      for (let i = 0; i < clientsToProcess.length; i++) {
        const client = clientsToProcess[i];
        const progressBase = 10 + (i / clientsToProcess.length) * 80;

        onProgress?.({
          current: progressBase,
          total: 100,
          message: `Processing ${client.name} (${i + 1}/${clientsToProcess.length})...`,
        });

        try {
          const result = await this.processClient(client, includeLastLogin);
          clientResults.push(result);
        } catch (error) {
          console.error(`Failed to process client ${client.name}:`, error);
          failedClients.push({
            clientName: client.name,
            error: error instanceof Error ? error.message : String(error),
          });
          // Continue with next client
        }
      }

      onProgress?.({ current: 90, total: 100, message: 'Aggregating results...' });

      // Combine all users
      let allUsers: UserRecord[] = [];
      for (const result of clientResults) {
        allUsers.push(...result.users);
      }

      // Apply user type filter
      if (userTypeFilter !== 'all') {
        allUsers = allUsers.filter((u) => u.userType === userTypeFilter);
      }

      // Sort by client name, then email
      allUsers.sort((a, b) => {
        if (a.clientName !== b.clientName) return a.clientName.localeCompare(b.clientName);
        return a.email.localeCompare(b.email);
      });

      // Calculate stats
      const adminCount = allUsers.filter((u) => u.userType === 'admin').length;
      const regularCount = allUsers.filter((u) => u.userType === 'user').length;

      // Determine client names for metadata
      const processedClientNames = clientResults.map((r) => r.clientName);

      // Create result object
      const result: AllUsersReportResult = {
        metadata: {
          clientNames: processedClientNames,
          exportDate: new Date().toISOString(),
          totalUsers: allUsers.length,
          adminUsers: adminCount,
          regularUsers: regularCount,
          clientsProcessed: clientResults.length,
          clientsFailed: failedClients.length,
        },
        users: allUsers,
        failedClients: failedClients.length > 0 ? failedClients : undefined,
      };

      onProgress?.({ current: 95, total: 100, message: 'Generating files...' });

      // Generate files
      const files: ToolResultFile[] = [];
      const filePrefix =
        clientsToProcess.length === 1 ? clientsToProcess[0].name : `all-clients-${clientsToProcess.length}`;

      if (outputFormat === 'json') {
        const jsonFile = this.generateJSON(allUsers, result.metadata, filePrefix);
        if (jsonFile) files.push(jsonFile);
      } else {
        const csvFile = this.generateCSV(allUsers, filePrefix);
        if (csvFile) files.push(csvFile);
      }

      onProgress?.({ current: 100, total: 100, message: 'Report generation completed!' });

      // Add message if no data or partial failures
      let message: string | undefined;
      if (allUsers.length === 0) {
        message = 'No users found for the selected clients.';
      } else if (failedClients.length > 0) {
        message = `Report generated with ${failedClients.length} client(s) failed: ${failedClients.map((f) => f.clientName).join(', ')}`;
      }

      return {
        result: message ? { ...result, message } : result,
        files,
      };
    } catch (error) {
      throw new Error(`Failed to generate users report: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Read every user of one client, a page at a time, through the client access Lambda. It finds
   * the client's account and user pool from its config, reads each person's groups and, when
   * asked, their last sign-in, and leaves the system user out.
   */
  private static async processClient(client: Client, includeLastLogin: boolean): Promise<ClientUsersResult> {
    const clientName = client.name;
    const users: UserRecord[] = [];
    let paginationToken: string | undefined;
    do {
      const page: ReportPage = await clientAccess<ReportPage>('users.reportPage', {
        clientName,
        paginationToken,
        includeLastLogin,
      });
      for (const u of page.users) {
        users.push({
          clientName,
          email: u.email || u.username,
          username: u.username,
          userType: u.isAdmin ? 'admin' : 'user',
          status: u.status || 'unknown',
          enabled: u.enabled,
          createdAt: u.createdAt || 'unknown',
          lastLoginAt: u.lastLoginAt,
        });
      }
      paginationToken = page.paginationToken;
    } while (paginationToken);

    return { clientName, users };
  }

  private static generateCSV(users: UserRecord[], filePrefix: string): ToolResultFile | null {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);

    const headers = ['Client', 'Email', 'Username', 'User Type', 'Status', 'Enabled', 'Created At', 'Last Login At'];

    const rows = users.map((u) => [
      u.clientName,
      u.email,
      u.username,
      u.userType,
      u.status,
      u.enabled ? 'Yes' : 'No',
      u.createdAt,
      u.lastLoginAt || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((cell) =>
            String(cell).includes(',') || String(cell).includes('"') ? `"${String(cell).replace(/"/g, '""')}"` : cell
          )
          .join(',')
      ),
    ].join('\n');

    return {
      name: `${filePrefix}-all-users-${timestamp}.csv`,
      content: csvContent,
      mimeType: 'text/csv',
      size: new Blob([csvContent]).size,
    };
  }

  private static generateJSON(
    users: UserRecord[],
    metadata: AllUsersReportResult['metadata'],
    filePrefix: string
  ): ToolResultFile | null {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);

    const exportData = {
      metadata,
      users,
    };

    const jsonContent = JSON.stringify(exportData, null, 2);

    return {
      name: `${filePrefix}-all-users-${timestamp}.json`,
      content: jsonContent,
      mimeType: 'application/json',
      size: new Blob([jsonContent]).size,
    };
  }
}
